import { IItemOrcamento } from "app/types/IItemOrcamento";
import { Dict } from "app/utils/Dict";
import React, { useEffect, useRef } from "react";
import {
  removerComponenteReact,
  renderizarComponentReact,
} from "../renderizarOuRemoverComponentReact";
import { MenuConsultaProdutosContainer } from "./MenuConsultasProdutoContainer";

const REGIAO_MENU = "menu-consulta-produtos-react";

/**
 * `menu-consulta-produtos-react` e UMA regiao do DOM disputada por ~21 componentes (a grid de itens
 * do orcamento, a pagina de pesquisa de produtos e cada sub-consulta: Estoque, Agregados, Ofertas,
 * Lotes, Tributacao, Sugerir Produto Agregado...). Todos chamam este hook.
 *
 * O problema que esta pilha resolve: antes, o cleanup de QUALQUER instancia fazia
 * `removerComponenteReact(REGIAO_MENU)` sem condicao. Quando uma sub-consulta se fecha sozinha —
 * por exemplo "Nenhum registro encontrado." chamando `onVoltar()`, que executa
 * `onRemover()` (= remove "conteudo-pagina") e desmonta a sub-consulta — esse cleanup apagava o menu
 * de quem AINDA ESTAVA MONTADO em outra regiao (a grid de itens vive em "itens-orcamento-react",
 * nao em "conteudo-pagina", entao ela nao remonta). Resultado: a linha continuava selecionada e o
 * menu de consultas desaparecia, sem nada para trazer de volta.
 *
 * Agora cada instancia se registra como "dona" do menu ao renderiza-lo, e ao desmontar apenas SAI
 * da pilha — quem assume e a dona anterior que continua montada. A regiao so e apagada quando nao
 * sobra ninguem.
 */
type DonoDoMenu = {
  token: object;
  renderizar: () => void;
};

const donosDoMenu: DonoDoMenu[] = [];

const registrarDono = (token: object, renderizar: () => void) => {
  const indice = donosDoMenu.findIndex((d) => d.token === token);
  if (indice >= 0) {
    donosDoMenu.splice(indice, 1);
  }
  donosDoMenu.push({ token, renderizar });
};

const removerDono = (token: object) => {
  const indice = donosDoMenu.findIndex((d) => d.token === token);
  if (indice >= 0) {
    donosDoMenu.splice(indice, 1);
  }
};

/**
 * Reconcilia a regiao com o topo da pilha. Ler o topo AQUI (e nao capturar no cleanup) faz a funcao
 * convergir sempre para "o menu exibido e o da dona atual", inclusive quando a propria instancia que
 * saiu volta a se registrar em seguida (efeito re-executando por troca de `itemProps`) — nesse caso
 * apenas re-renderiza o mesmo conteudo, que o React reconcilia sem custo.
 */
const restaurarMenuDaDonaAtual = () => {
  const dona = donosDoMenu[donosDoMenu.length - 1];
  if (dona) {
    dona.renderizar();
  } else {
    removerComponenteReact(REGIAO_MENU);
  }
};

export function useMenuConsultaProduto(passosProps: Dict<string, () => void>, onVoltarProps: () => void, itemProps?: IItemOrcamento, foraOrcamento?: boolean, integracaoHabilitada: boolean = true) {

  const tokenDona = useRef({});

  const onRenderizarMenuConsultaProdutoReact = (passos?: Dict<string, () => void>) => {
    // 491123 — quando a página de pesquisa é montada num host invisível (botão flutuante da cesta
    // aberto fora da tela de pesquisa), "menu-consulta-produtos-react" é uma região fixa e VISÍVEL
    // do layout principal — não pertence a este host oculto. Sem este guard, toda montagem aqui
    // chamaria removerComponenteReact nela, apagando um menu de ações que porventura já estivesse
    // aberto para o usuário em outra tela (ex: item selecionado na grid de Itens do orçamento).
    if (!integracaoHabilitada) {
      return
    }

    if (itemProps == null) {
      removerComponenteReact(REGIAO_MENU)
    } else {
      renderizarComponentReact(
        <MenuConsultaProdutosContainer
          foraOrcamento={foraOrcamento}
          item={itemProps}
          onSelecionadoMenu={() => {
            $("[container]").hide();
          }}
          onVoltar={onVoltarProps}
          passos={passos ?? passosProps}
        />,
        REGIAO_MENU,
        true
      );
    }
  }

  useEffect(() => {
    if (!integracaoHabilitada) {
      return;
    }

    const token = tokenDona.current;

    const timer = setTimeout(() => {
      registrarDono(token, () => onRenderizarMenuConsultaProdutoReact());
      onRenderizarMenuConsultaProdutoReact();
    });

    return () => {
      clearTimeout(timer);
      removerDono(token);
      // Assíncrono de propósito: no fluxo "sub-consulta se fecha e a tela anterior remonta", o
      // cleanup da sub roda ANTES da nova dona se registrar. Esperar um tick deixa a nova dona
      // entrar na pilha primeiro, então a restauração já encontra o dono certo no topo.
      setTimeout(restaurarMenuDaDonaAtual);
    };
  }, [itemProps]);

  return onRenderizarMenuConsultaProdutoReact;
}
